import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone } from "@phosphor-icons/react";

const ROLES = ["ADMIN", "MANAGER", "TASK_OWNER", "REVIEWER", "EMPLOYEE"];

export default function Broadcast() {
  const [form, setForm] = useState({ title: "", body: "", link: "", roles: [] });
  const [busy, setBusy] = useState(false);

  const toggleRole = (r) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));
  };

  const send = async () => {
    if (!form.title || !form.body) {
      toast.error("Title and body are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.roles.length === 0) payload.roles = null;
      if (!payload.link) delete payload.link;
      const { data } = await api.post("/admin/broadcast", payload);
      toast.success(`Sent to ${data.count} people`);
      setForm({ title: "", body: "", link: "", roles: [] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-background p-5 space-y-4" data-testid="broadcast-page">
      <div className="flex items-center gap-3">
        <Megaphone size={22} className="text-[#E1252A]" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Announcement</div>
          <h3 className="text-lg font-semibold tracking-tight">Broadcast to the org</h3>
          <p className="text-xs text-zinc-500 mt-1">Every recipient will receive an in-app notification.</p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Title</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="broadcast-title-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Body</Label>
          <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-sm" data-testid="broadcast-body-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Link (optional)</Label>
          <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="/marketplace or https://…" className="rounded-sm" data-testid="broadcast-link-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Target roles (leave empty for everyone)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {ROLES.map((r) => {
              const on = form.roles.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  className={`px-3 py-1 rounded-sm border text-xs uppercase tracking-wider transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border"}`}
                  data-testid={`broadcast-role-${r}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={send} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="broadcast-send-btn">
          {busy ? "Sending…" : "Send announcement"}
        </Button>
      </div>
    </div>
  );
}
